import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react'
import {SocketContext} from './socket.init'
import useAdminNotifications from '../hooks/useAdminNotifications'
import {
  AdminNotification,
  AdminNotificationSocketPayload,
  AdminNotificationTypeCount,
} from '../types/admin-notification.type'
import {
  AdminNotificationGroupKey,
  getGroupTypes,
  isInGroup,
} from '../components/admin-notifications/notification-links'

const PAGE_SIZE = 20
const POLL_INTERVAL_MS = 60000

interface AdminNotificationContextProps {
  notifications: AdminNotification[]
  unreadCount: number
  countsByType: AdminNotificationTypeCount[]
  activeGroup: AdminNotificationGroupKey
  unreadOnly: boolean
  loading: boolean
  loadingMore: boolean
  hasNextPage: boolean
  totalDocs: number
  error: string | null
  setActiveGroup: (group: AdminNotificationGroupKey) => void
  setUnreadOnly: (value: boolean) => void
  loadMore: () => void
  refresh: () => void
  /** Re-fetch unreadCount / countsByType without reloading the inbox list. */
  syncUnread: () => void
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
}

const AdminNotificationContext = createContext<AdminNotificationContextProps>({
  notifications: [],
  unreadCount: 0,
  countsByType: [],
  activeGroup: 'all',
  unreadOnly: false,
  loading: false,
  loadingMore: false,
  hasNextPage: false,
  totalDocs: 0,
  error: null,
  setActiveGroup: () => {},
  setUnreadOnly: () => {},
  loadMore: () => {},
  refresh: () => {},
  syncUnread: () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
})

const AdminNotificationProvider = ({children}: {children: React.ReactNode}) => {
  const {socket} = useContext(SocketContext)
  const {fetchNotifications, fetchUnreadCount, markRead, markAllRead} = useAdminNotifications()

  const [notifications, setNotifications] = useState<AdminNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [countsByType, setCountsByType] = useState<AdminNotificationTypeCount[]>([])
  const [activeGroup, setActiveGroup] = useState<AdminNotificationGroupKey>('all')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [totalDocs, setTotalDocs] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [socketConnected, setSocketConnected] = useState(false)

  // Socket handlers are registered once but need the current filters, so the
  // latest values are mirrored into refs.
  const activeGroupRef = useRef(activeGroup)
  const unreadOnlyRef = useRef(unreadOnly)
  activeGroupRef.current = activeGroup
  unreadOnlyRef.current = unreadOnly

  const fetchUnreadCountRef = useRef(fetchUnreadCount)
  const fetchNotificationsRef = useRef(fetchNotifications)
  const markReadRef = useRef(markRead)
  const markAllReadRef = useRef(markAllRead)
  fetchUnreadCountRef.current = fetchUnreadCount
  fetchNotificationsRef.current = fetchNotifications
  markReadRef.current = markRead
  markAllReadRef.current = markAllRead

  const syncUnreadCount = useCallback(async () => {
    try {
      const result = await fetchUnreadCountRef.current()
      setUnreadCount(result.total ?? 0)
      setCountsByType(result.countsByType ?? [])
    } catch (err) {
      console.error('Failed to sync admin notification unread count', err)
    }
  }, [])

  const loadPage = useCallback(
    async (targetPage: number, group: AdminNotificationGroupKey, onlyUnread: boolean) => {
      const isFirstPage = targetPage === 1
      if (isFirstPage) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }
      setError(null)

      try {
        const result = await fetchNotificationsRef.current({
          page: targetPage,
          pageSize: PAGE_SIZE,
          types: getGroupTypes(group),
          unreadOnly: onlyUnread || undefined,
        })

        const incoming = result.data ?? []
        setNotifications((prev) => {
          if (isFirstPage) return incoming
          const existingIds = new Set(prev.map((item) => item._id))
          return [...prev, ...incoming.filter((item) => !existingIds.has(item._id))]
        })
        setPage(result.page ?? targetPage)
        setHasNextPage(Boolean(result.hasNextPage))
        setTotalDocs(result.totalDocs ?? 0)
        // Badge comes only from getAdminNotificationUnreadCountR — avoid racing list.unreadCount.
        await syncUnreadCount()
      } catch (err: any) {
        setError(err?.message ?? 'Failed to load notifications')
        if (isFirstPage) {
          setNotifications([])
          setHasNextPage(false)
          setTotalDocs(0)
        }
        await syncUnreadCount()
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [syncUnreadCount]
  )

  useEffect(() => {
    void loadPage(1, activeGroup, unreadOnly)
  }, [activeGroup, unreadOnly, loadPage])

  // Socket payloads carry no isRead: anything arriving this way is unread for
  // the receiving admin.
  const applyIncoming = useCallback(
    (payload: AdminNotificationSocketPayload, isUpdate: boolean) => {
      if (!payload?._id) return

      const incoming: AdminNotification = {...payload, isRead: false}
      let existing: AdminNotification | undefined

      setNotifications((prev) => {
        existing = prev.find((item) => item._id === incoming._id)
        const rest = prev.filter((item) => item._id !== incoming._id)

        // Keep the entry out of the visible list when it falls outside the
        // active filters, but still let the badge update.
        if (!isInGroup(incoming, activeGroupRef.current)) return rest

        return [incoming, ...rest]
      })

      if (!isUpdate) {
        setUnreadCount((prev) => prev + 1)
        return
      }

      if (existing) {
        // A bump clears read state, so only a previously read copy adds to the badge.
        if (existing.isRead) setUnreadCount((prev) => prev + 1)
      } else {
        // The bumped entry was never loaded, so its prior read state is unknown.
        void syncUnreadCount()
      }
    },
    [syncUnreadCount]
  )

  useEffect(() => {
    if (!socket) return

    const handleNew = (payload: {notification: AdminNotificationSocketPayload}) =>
      applyIncoming(payload?.notification, false)
    const handleUpdated = (payload: {notification: AdminNotificationSocketPayload}) =>
      applyIncoming(payload?.notification, true)
    const handleConnect = () => {
      setSocketConnected(true)
      // The local tally cannot be trusted across a gap in delivery.
      void syncUnreadCount()
    }
    const handleDisconnect = () => setSocketConnected(false)

    setSocketConnected(socket.connected)

    socket.on('admin-notification-new', handleNew)
    socket.on('admin-notification-updated', handleUpdated)
    socket.on('connect', handleConnect)
    socket.on('disconnect', handleDisconnect)

    return () => {
      socket.off('admin-notification-new', handleNew)
      socket.off('admin-notification-updated', handleUpdated)
      socket.off('connect', handleConnect)
      socket.off('disconnect', handleDisconnect)
    }
  }, [socket, applyIncoming, syncUnreadCount])

  // Fallback for a missing or disconnected socket.
  useEffect(() => {
    if (socketConnected) return

    const interval = setInterval(() => {
      void syncUnreadCount()
    }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [socketConnected, syncUnreadCount])

  const markAsRead = useCallback(
    async (id: string) => {
      const target = notifications.find((item) => item._id === id)
      if (!target || target.isRead) return

      setNotifications((prev) =>
        prev.map((item) => (item._id === id ? {...item, isRead: true} : item))
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))

      try {
        const result = await markReadRef.current([id])
        if (result) setUnreadCount(result.unreadTotal ?? 0)
        await syncUnreadCount()
      } catch (err: any) {
        setNotifications((prev) =>
          prev.map((item) => (item._id === id ? {...item, isRead: false} : item))
        )
        setUnreadCount((prev) => prev + 1)
        setError(err?.message ?? 'Failed to mark notification as read')
      }
    },
    [notifications, syncUnreadCount]
  )

  const markAllAsRead = useCallback(async () => {
    const previous = notifications
    const previousUnread = unreadCount
    const previousCounts = countsByType

    // Everything loaded belongs to the active group, so all of it is cleared.
    setNotifications((prev) => prev.map((item) => ({...item, isRead: true})))
    // Optimistic clear when marking All — covers ghost unread with empty list.
    if (activeGroup === 'all') {
      setUnreadCount(0)
      setCountsByType([])
    }

    try {
      const result = await markAllReadRef.current(getGroupTypes(activeGroup))
      setUnreadCount(result?.unreadTotal ?? 0)
      await syncUnreadCount()
      if (unreadOnly || previous.length === 0) {
        await loadPage(1, activeGroup, unreadOnly)
      }
    } catch (err: any) {
      setNotifications(previous)
      setUnreadCount(previousUnread)
      setCountsByType(previousCounts)
      setError(err?.message ?? 'Failed to mark all notifications as read')
    }
  }, [activeGroup, notifications, unreadCount, countsByType, unreadOnly, loadPage, syncUnreadCount])

  const loadMore = useCallback(() => {
    if (!hasNextPage || loadingMore) return
    void loadPage(page + 1, activeGroup, unreadOnly)
  }, [hasNextPage, loadingMore, page, activeGroup, unreadOnly, loadPage])

  const refresh = useCallback(() => {
    void loadPage(1, activeGroup, unreadOnly)
  }, [activeGroup, unreadOnly, loadPage])

  return (
    <AdminNotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        countsByType,
        activeGroup,
        unreadOnly,
        loading,
        loadingMore,
        hasNextPage,
        totalDocs,
        error,
        setActiveGroup,
        setUnreadOnly,
        loadMore,
        refresh,
        syncUnread: syncUnreadCount,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </AdminNotificationContext.Provider>
  )
}

const useAdminNotificationPool = () => useContext(AdminNotificationContext)

export {AdminNotificationContext, AdminNotificationProvider, useAdminNotificationPool}
